import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertTriangle,
  Ban,
  CheckCircle,
  Clock,
  Eye,
  RefreshCw,
  Search,
  ShieldCheck,
  UserCheck,
  Users,
  X,
  XCircle,
} from "lucide-react";
import myAxios from "../AxiosUrl";
import Loading from "../Loading/Loading";
import styles from "./PlayerPhotoSubmissions.module.css";

const PAGE_SIZE = 20;

const statusOptions = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "all", label: "All" },
];

const submitterRoleLabels = {
  player: "Player",
  parent_guardian: "Parent / guardian",
  representative: "Representative",
  club_staff: "Club staff",
  other: "Other",
};

const confidenceLabels = {
  high: "High match",
  medium: "Medium match",
  low: "Low match",
  none: "No match",
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getSubmissionId = (submission) => submission?._id || submission?.id || "";

const getDisplayName = (player) =>
  player?.knownName ||
  [player?.firstName, player?.lastName].filter(Boolean).join(" ") ||
  player?.displayName ||
  player?.id ||
  "Unknown player";

const getCandidateId = (candidate) =>
  candidate?.playerId || candidate?.player?.id || candidate?.id || "";

const getCandidateName = (candidate) =>
  candidate?.displayName ||
  getDisplayName(candidate?.player) ||
  candidate?.playerId ||
  "Unknown player";

const getCandidateClub = (candidate) =>
  candidate?.clubName || candidate?.player?.clubName || "-";

const getCandidatePosition = (candidate) =>
  candidate?.position || candidate?.player?.position || "-";

const normalizeError = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

const getPublicApiUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;

  const publicBase = String(myAxios.defaults.baseURL || "").replace(
    /\/admin\/api\/v1\/?$/,
    ""
  );
  if (!publicBase) return path;

  return `${publicBase.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
};

const getConfidenceClass = (confidence) => {
  if (confidence === "high") return styles.confidenceHigh;
  if (confidence === "medium") return styles.confidenceMedium;
  if (confidence === "low") return styles.confidenceLow;
  return styles.confidenceNone;
};

const getStatusClass = (status) => {
  if (status === "approved") return styles.statusApproved;
  if (status === "rejected") return styles.statusRejected;
  return styles.statusPending;
};

const PlayerPhotoSubmissions = () => {
  const [submissions, setSubmissions] = useState([]);
  const [summary, setSummary] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    limit: PAGE_SIZE,
  });
  const [status, setStatus] = useState("pending");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [selectedPlayerId, setSelectedPlayerId] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [playerSearch, setPlayerSearch] = useState("");
  const [playerResults, setPlayerResults] = useState([]);
  const [playerSearchLoading, setPlayerSearchLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [alert, setAlert] = useState(null);

  const selectedId = getSubmissionId(selectedSubmission);

  const fetchSubmissions = useCallback(
    async ({ silent = false } = {}) => {
      try {
        if (silent) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }

        const params = new URLSearchParams({
          status,
          page: String(page),
          limit: String(PAGE_SIZE),
        });

        if (search.trim()) {
          params.set("search", search.trim());
        }

        const response = await myAxios.get(
          `/player-photo-submissions?${params.toString()}`
        );

        const nextSubmissions = response.data?.data || [];
        setSubmissions(nextSubmissions);
        setSummary(
          response.data?.summary || {
            pending: 0,
            approved: 0,
            rejected: 0,
          }
        );
        setPagination(
          response.data?.pagination || {
            currentPage: page,
            totalPages: 1,
            totalItems: nextSubmissions.length,
            limit: PAGE_SIZE,
          }
        );

        setSelectedSubmission((current) => {
          if (!current && nextSubmissions.length > 0) return nextSubmissions[0];
          const currentId = getSubmissionId(current);
          return (
            nextSubmissions.find(
              (submission) => getSubmissionId(submission) === currentId
            ) ||
            nextSubmissions[0] ||
            null
          );
        });
      } catch (error) {
        setAlert({
          type: "error",
          message: normalizeError(error, "Failed to load photo submissions"),
        });
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [page, search, status]
  );

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  useEffect(() => {
    const suggested = selectedSubmission?.suggestedPlayers || [];
    const firstSuggestedId = getCandidateId(suggested[0]);
    const approvedId = selectedSubmission?.review?.approvedPlayerId || "";
    setSelectedPlayerId(approvedId || firstSuggestedId || "");
    setAdminNotes(selectedSubmission?.review?.adminNotes || "");
    setRejectionReason(selectedSubmission?.review?.rejectionReason || "");
    setPlayerSearch("");
    setPlayerResults([]);
  }, [selectedSubmission]);

  useEffect(() => {
    const trimmedSearch = playerSearch.trim();
    if (trimmedSearch.length < 2) {
      setPlayerResults([]);
      return undefined;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        setPlayerSearchLoading(true);
        const params = new URLSearchParams({
          search: trimmedSearch,
          limit: "8",
          page: "1",
        });
        const response = await myAxios.get(
          `/tournament/players?${params.toString()}`
        );
        if (!cancelled) {
          setPlayerResults(response.data?.data || []);
        }
      } catch (error) {
        if (!cancelled) {
          setPlayerResults([]);
        }
      } finally {
        if (!cancelled) {
          setPlayerSearchLoading(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [playerSearch]);

  const selectedCandidate = useMemo(() => {
    const candidates = selectedSubmission?.suggestedPlayers || [];
    return candidates.find((candidate) => getCandidateId(candidate) === selectedPlayerId);
  }, [selectedPlayerId, selectedSubmission]);

  const selectedOutsideSuggestions = useMemo(() => {
    if (!selectedSubmission || !selectedPlayerId) return false;
    const suggestedIds = new Set(
      (selectedSubmission.suggestedPlayers || []).map(getCandidateId)
    );
    return suggestedIds.size > 0 && !suggestedIds.has(selectedPlayerId);
  }, [selectedPlayerId, selectedSubmission]);

  const selectSubmission = (submission) => {
    setSelectedSubmission(submission);
    setAlert(null);
  };

  const resetToFirstPage = (nextStatus = status) => {
    setStatus(nextStatus);
    setPage(1);
  };

  const approveSubmission = async () => {
    if (!selectedId || !selectedPlayerId) {
      setAlert({
        type: "error",
        message: "Select the real player before approving this photo.",
      });
      return;
    }

    try {
      setActionLoading(true);
      const response = await myAxios.patch(
        `/player-photo-submissions/${selectedId}/approve`,
        {
          playerId: selectedPlayerId,
          adminNotes,
        }
      );

      setAlert({
        type: "success",
        message: response.data?.message || "Photo submission approved.",
      });
      await fetchSubmissions({ silent: true });
    } catch (error) {
      setAlert({
        type: "error",
        message: normalizeError(error, "Failed to approve submission"),
      });
    } finally {
      setActionLoading(false);
    }
  };

  const rejectSubmission = async () => {
    if (!selectedId) return;

    try {
      setActionLoading(true);
      const response = await myAxios.patch(
        `/player-photo-submissions/${selectedId}/reject`,
        {
          rejectionReason,
          adminNotes,
        }
      );

      setAlert({
        type: "success",
        message: response.data?.message || "Photo submission rejected.",
      });
      await fetchSubmissions({ silent: true });
    } catch (error) {
      setAlert({
        type: "error",
        message: normalizeError(error, "Failed to reject submission"),
      });
    } finally {
      setActionLoading(false);
    }
  };

  const renderStatCard = (label, value, icon, className) => (
    <div className={styles.statCard}>
      <div className={`${styles.statIcon} ${className}`}>{icon}</div>
      <div>
        <strong>{value || 0}</strong>
        <span>{label}</span>
      </div>
    </div>
  );

  if (loading) {
    return <Loading />;
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <p className={styles.kicker}>Moderation</p>
          <h1>Player Photo Submissions</h1>
          <p>
            Review uploaded player photos, verify identity signals, and apply
            approved images to the correct TopSekk player profile.
          </p>
        </div>
        <button
          type="button"
          className={styles.refreshButton}
          onClick={() => fetchSubmissions({ silent: true })}
          disabled={refreshing}
        >
          <RefreshCw size={16} className={refreshing ? styles.spin : ""} />
          Refresh
        </button>
      </div>

      <div className={styles.statsGrid}>
        {renderStatCard(
          "Pending",
          summary.pending,
          <Clock size={22} />,
          styles.statPending
        )}
        {renderStatCard(
          "Approved",
          summary.approved,
          <CheckCircle size={22} />,
          styles.statApproved
        )}
        {renderStatCard(
          "Rejected",
          summary.rejected,
          <XCircle size={22} />,
          styles.statRejected
        )}
        {renderStatCard(
          "Total",
          pagination.totalItems,
          <Users size={22} />,
          styles.statTotal
        )}
      </div>

      <AnimatePresence>
        {alert && (
          <motion.div
            className={`${styles.alert} ${
              alert.type === "success" ? styles.alertSuccess : styles.alertError
            }`}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
          >
            {alert.type === "success" ? (
              <CheckCircle size={18} />
            ) : (
              <AlertTriangle size={18} />
            )}
            <span>{alert.message}</span>
            <button type="button" onClick={() => setAlert(null)}>
              <X size={16} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <div className={styles.controls}>
        <label className={styles.searchBox}>
          <Search size={18} />
          <input
            type="search"
            value={search}
            placeholder="Search name, email, club, code, player id..."
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
          />
        </label>

        <select
          className={styles.select}
          value={status}
          onChange={(event) => resetToFirstPage(event.target.value)}
        >
          {statusOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.workspace}>
        <section className={styles.listPanel}>
          <div className={styles.panelHeader}>
            <h2>Submissions</h2>
            <span>{pagination.totalItems || 0} results</span>
          </div>

          <div className={styles.submissionList}>
            {submissions.length === 0 ? (
              <div className={styles.emptyState}>
                <ShieldCheck size={34} />
                <strong>No submissions found</strong>
                <span>Try another filter or refresh the list.</span>
              </div>
            ) : (
              submissions.map((submission) => {
                const id = getSubmissionId(submission);
                const confidence = submission.matchReview?.confidence || "none";
                const isSelected = id === selectedId;

                return (
                  <button
                    type="button"
                    key={id}
                    className={`${styles.submissionCard} ${
                      isSelected ? styles.selectedCard : ""
                    }`}
                    onClick={() => selectSubmission(submission)}
                  >
                    <img
                      src={submission.image?.url}
                      alt={submission.fullName || "Player submission"}
                      className={styles.thumbnail}
                    />
                    <div className={styles.submissionMeta}>
                      <div className={styles.submissionTitleRow}>
                        <strong>{submission.fullName || "Unnamed player"}</strong>
                        <span
                          className={`${styles.statusBadge} ${getStatusClass(
                            submission.status
                          )}`}
                        >
                          {submission.status || "pending"}
                        </span>
                      </div>
                      <span>{submission.currentClub || "-"}</span>
                      <span>{submission.email || "-"}</span>
                      <div className={styles.cardFooter}>
                        <span
                          className={`${styles.confidenceBadge} ${getConfidenceClass(
                            confidence
                          )}`}
                        >
                          {confidenceLabels[confidence] || confidenceLabels.none}
                        </span>
                        <span>{formatDateTime(submission.createdAt)}</span>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {pagination.totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((value) => Math.max(1, value - 1))}
              >
                Previous
              </button>
              <span>
                Page {pagination.currentPage || page} of{" "}
                {pagination.totalPages || 1}
              </span>
              <button
                type="button"
                disabled={page >= pagination.totalPages}
                onClick={() =>
                  setPage((value) =>
                    Math.min(pagination.totalPages || value, value + 1)
                  )
                }
              >
                Next
              </button>
            </div>
          )}
        </section>

        <section className={styles.detailPanel}>
          {!selectedSubmission ? (
            <div className={styles.emptyState}>
              <Eye size={34} />
              <strong>Select a submission</strong>
              <span>Submission details and review actions will appear here.</span>
            </div>
          ) : (
            <>
              <div className={styles.detailHeader}>
                <div>
                  <h2>{selectedSubmission.fullName}</h2>
                  <span>{getSubmissionId(selectedSubmission)}</span>
                </div>
                <span
                  className={`${styles.statusBadge} ${getStatusClass(
                    selectedSubmission.status
                  )}`}
                >
                  {selectedSubmission.status}
                </span>
              </div>

              <div className={styles.reviewGrid}>
                <div className={styles.photoPreview}>
                  <img
                    src={selectedSubmission.image?.url}
                    alt={selectedSubmission.fullName || "Submitted player"}
                  />
                  <a
                    href={selectedSubmission.image?.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open image
                  </a>
                </div>

                <div className={styles.identityBox}>
                  <div>
                    <span>Full name</span>
                    <strong>{selectedSubmission.fullName || "-"}</strong>
                  </div>
                  <div>
                    <span>Club</span>
                    <strong>{selectedSubmission.currentClub || "-"}</strong>
                  </div>
                  <div>
                    <span>Date of birth</span>
                    <strong>{formatDate(selectedSubmission.dateOfBirth)}</strong>
                  </div>
                  <div>
                    <span>Email</span>
                    <strong>{selectedSubmission.email || "-"}</strong>
                  </div>
                  <div>
                    <span>Copyright owner</span>
                    <strong>{selectedSubmission.copyrightOwner || "-"}</strong>
                  </div>
                  <div>
                    <span>Photo credit</span>
                    <strong>{selectedSubmission.photoCredit || "-"}</strong>
                  </div>
                  <div>
                    <span>Submitter</span>
                    <strong>
                      {submitterRoleLabels[selectedSubmission.submitterRole] ||
                        selectedSubmission.submitterRole ||
                        "-"}
                    </strong>
                  </div>
                  <div>
                    <span>Verification code</span>
                    <strong>
                      {selectedSubmission.verificationCode ||
                        selectedSubmission.clubCode ||
                        "Not provided"}
                    </strong>
                  </div>
                  <div>
                    <span>Consent version</span>
                    <strong>
                      {selectedSubmission.legal?.consentVersion || "-"}
                    </strong>
                  </div>
                  <div>
                    <span>Consent accepted</span>
                    <strong>
                      {formatDateTime(selectedSubmission.legal?.acceptedAt)}
                    </strong>
                  </div>
                  <div>
                    <span>Consent document</span>
                    <strong>
                      {selectedSubmission.legal?.consentDocumentUrl ? (
                        <a
                          href={getPublicApiUrl(
                            selectedSubmission.legal.consentDocumentUrl
                          )}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Open consent
                        </a>
                      ) : (
                        "-"
                      )}
                    </strong>
                  </div>
                  {selectedSubmission.message && (
                    <div className={styles.wideIdentity}>
                      <span>Submission note</span>
                      <strong>{selectedSubmission.message}</strong>
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.matchPanel}>
                <div className={styles.matchHeader}>
                  <div>
                    <span>Match confidence</span>
                    <strong>
                      {confidenceLabels[
                        selectedSubmission.matchReview?.confidence || "none"
                      ] || confidenceLabels.none}
                    </strong>
                  </div>
                  <span
                    className={`${styles.scoreBadge} ${getConfidenceClass(
                      selectedSubmission.matchReview?.confidence || "none"
                    )}`}
                  >
                    Score {selectedSubmission.matchReview?.score || 0}
                  </span>
                </div>

                {(selectedSubmission.matchReview?.flags || []).length > 0 && (
                  <div className={styles.flags}>
                    {selectedSubmission.matchReview.flags.map((flag) => (
                      <span key={flag}>{flag.replace(/_/g, " ")}</span>
                    ))}
                  </div>
                )}
              </div>

              <div className={styles.sectionTitle}>
                <UserCheck size={18} />
                <h3>Select real player</h3>
              </div>

              <div className={styles.candidates}>
                {(selectedSubmission.suggestedPlayers || []).length === 0 ? (
                  <div className={styles.noCandidates}>
                    No suggested players. Search manually below.
                  </div>
                ) : (
                  selectedSubmission.suggestedPlayers.map((candidate) => {
                    const candidateId = getCandidateId(candidate);
                    const checked = selectedPlayerId === candidateId;

                    return (
                      <label
                        key={candidateId}
                        className={`${styles.candidateCard} ${
                          checked ? styles.candidateSelected : ""
                        }`}
                      >
                        <input
                          type="radio"
                          name="selectedPlayer"
                          checked={checked}
                          onChange={() => setSelectedPlayerId(candidateId)}
                        />
                        <div>
                          <strong>{getCandidateName(candidate)}</strong>
                          <span>{candidateId}</span>
                        </div>
                        <div>
                          <span>{getCandidateClub(candidate)}</span>
                          <small>{getCandidatePosition(candidate)}</small>
                        </div>
                        <span className={styles.candidateScore}>
                          {candidate.score || 0}
                        </span>
                      </label>
                    );
                  })
                )}
              </div>

              <div className={styles.manualSearch}>
                <label>
                  Search another player
                  <input
                    type="search"
                    value={playerSearch}
                    placeholder="Type player name, club, or id"
                    onChange={(event) => setPlayerSearch(event.target.value)}
                  />
                </label>
                {playerSearchLoading && (
                  <span className={styles.searching}>Searching...</span>
                )}
                {playerResults.length > 0 && (
                  <div className={styles.searchResults}>
                    {playerResults.map((player) => (
                      <button
                        type="button"
                        key={player.id}
                        onClick={() => setSelectedPlayerId(player.id)}
                        className={
                          selectedPlayerId === player.id ? styles.resultSelected : ""
                        }
                      >
                        <span>
                          <strong>{getDisplayName(player)}</strong>
                          <small>{player.clubName || "-"}</small>
                        </span>
                        <code>{player.id}</code>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {selectedOutsideSuggestions && (
                <div className={styles.warningBox}>
                  <AlertTriangle size={18} />
                  Selected player is outside the suggested matches. Double-check
                  before approval.
                </div>
              )}

              {selectedCandidate && (
                <div className={styles.selectedSummary}>
                  <span>Selected suggested player</span>
                  <strong>{getCandidateName(selectedCandidate)}</strong>
                  <small>{selectedPlayerId}</small>
                </div>
              )}

              <div className={styles.notesGrid}>
                <label>
                  Admin notes
                  <textarea
                    value={adminNotes}
                    placeholder="Optional internal note"
                    onChange={(event) => setAdminNotes(event.target.value)}
                  />
                </label>
                <label>
                  Rejection reason
                  <textarea
                    value={rejectionReason}
                    placeholder="Required only when rejecting"
                    onChange={(event) => setRejectionReason(event.target.value)}
                  />
                </label>
              </div>

              <div className={styles.actions}>
                <button
                  type="button"
                  className={styles.rejectButton}
                  onClick={rejectSubmission}
                  disabled={
                    actionLoading || selectedSubmission.status !== "pending"
                  }
                >
                  <Ban size={17} />
                  Reject
                </button>
                <button
                  type="button"
                  className={styles.approveButton}
                  onClick={approveSubmission}
                  disabled={
                    actionLoading ||
                    selectedSubmission.status !== "pending" ||
                    !selectedPlayerId
                  }
                >
                  <CheckCircle size={17} />
                  Approve photo
                </button>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default PlayerPhotoSubmissions;
